import { css } from '@emotion/css';
import { UsageTrackerEvent, usageTracker } from '@volumental/analytic';
import giftCard from '@volumental/assets/images/Feedback/GiftCard.png';
import {
  Button,
  ErrorText,
  Headline,
  isValidEmail,
  Text,
  TextButton,
} from '@volumental/common';
import { type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type SizeLocale } from '../../../api/gql/graphql';
import { Input } from '../../../components/accessible-components';
import { Modal } from '../../../components/modal/modal';
import { useScannerStore, useSessionStore } from '../../../store';

const imageStyle = css`
  border-radius: 12px;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;
const imageContainerStyle = css`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-items: center;
  justify-self: center;
  align-self: center;
  justify-content: center;
  margin: 24px 0px 10px 0px;
`;
const buttonContainer = css`
  margin: 16px 0px 20px 0px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

interface EmailCollectorProps {
  show: boolean;
  onClose: (feedback: boolean) => void;
  pdpDetail?: { local: SizeLocale; gender?: string; recommendedSize: string };
  scanID?: string;
  previousPurchaseID?: string;
}

export function EmailCollector({
  show,
  onClose,
  pdpDetail,
  scanID,
  previousPurchaseID,
}: EmailCollectorProps): JSX.Element {
  const { mode, productID } = useSessionStore((store) => store);
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const { flowType } = useScannerStore((store) => store);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const handleEmailChange = (event: string): void => {
    if (event.length === 0) {
      setEmailError(false);
    } else {
      setEmailError(!isValidEmail(event.toLowerCase()));
    }
    setEmail(event.toLowerCase());
  };

  const uploadFeeback = (): void => {
    // uploadFeedback
    const feedback: Record<string, string | undefined> = {
      Email: email,
      recommended_size: pdpDetail?.recommendedSize,
      productID: productID ?? 'UNKNOWN',
      size_local: pdpDetail?.local,
      product_gender: pdpDetail?.gender,
      scanID: scanID ?? '',
      previous_purchase_id: previousPurchaseID,
      sizing_flow_type: flowType,
      experience: mode,
    };
    usageTracker.track(UsageTrackerEvent.EmailCollectorWithGift, feedback);

    onClose(true);
  };

  const submitEmail = (): void => {
    setEmailError(!isValidEmail(email));
    if (isValidEmail(email)) {
      setError(false);
      uploadFeeback();
    }
  };

  const onSkip = (): void => {
    onClose(false);
  };

  return (
    <Modal
      show={show}
      close={onSkip}
      modalTitle={t('emailCollectorFeedback.titleWithGift')}
    >
      <Headline style={{ margin: '0px 0px 8px 0px' }}>
        {t('emailCollectorFeedback.titleWithGift')}
      </Headline>
      <Text style={{ fontSize: '16px' }}>
        {t('emailCollectorFeedback.descriptionWithGift')}
      </Text>
      <div className={imageContainerStyle}>
        <img
          className={imageStyle}
          src={giftCard}
          alt={t('imageAlt.giftCard')}
        />
      </div>
      {
        <Text style={{ fontSize: '12px', lineHeight: 'undefined' }}>
          {t('emailCollectorFeedback.imageDescriptionWithGift')}
        </Text>
      }
      <div
        style={{
          paddingTop: '24px',
        }}
      >
        <Input
          value={email}
          onChange={handleEmailChange}
          error={emailError}
          type="email"
          crossClick={() => {
            setEmail('');
            setEmailError(false);
          }}
          placeholder={t('emailCollectorFeedback.emailPlaceholder')}
          applyBackground
          boarderOnFocus
          ariaLabel={t('ariaLabels.emailInput')}
        />
        {error && <ErrorText>{t('SometingWentWrong')}</ErrorText>}
      </div>
      <div className={buttonContainer}>
        <Button
          type="submit"
          disabled={!isValidEmail(email)}
          onClick={submitEmail}
        >
          {t('button.signUp')}
        </Button>
        <TextButton onClick={onSkip}>{t('button.skip')}</TextButton>
      </div>
    </Modal>
  );
}
